"use client"

import { useId } from "react"
import { Sparkles } from "lucide-react"

import { Label } from "@/components/ui/label"
import { useAuth } from "@/hooks/auth/use-auth"
import { useSelfLearningQuery } from "@/hooks/queries/self-learning"
import { qualityOffNotice } from "@/lib/conversation-quality/access-display"
import { actsAsSuggestOnly } from "@/lib/conversation-quality/self-learning"
import {
  IN_EVERY_MODE_NOTE,
  NO_OWN_INSTRUCTIONS_LINE,
  SELF_LEARNING_COPY,
  SELF_LEARNING_LABEL,
  learningSinceLine,
} from "@/lib/conversation-quality/self-learning-display"

import { SelfLearningArticlesSwitch } from "./self-learning-articles-switch"
import { ModeWarning, SelfLearningModeSelect } from "./self-learning-mode-select"

/** An agent's Self-learning mode with what it means, for the settings of each agent kind that learns. */
export function SelfLearningControl({
  agentId,
  agentName,
  canManage,
  hasOwnInstructions,
}: {
  agentId: string
  agentName: string
  canManage: boolean
  /** `null` while unknown. */
  hasOwnInstructions: boolean | null
}) {
  const selectId = useId()
  const detailsId = useId()
  const { user } = useAuth()
  const { data, error } = useSelfLearningQuery(agentId)

  if (error) return <p className="text-sm text-destructive">{error.message}</p>
  if (!data) return null

  const copy = SELF_LEARNING_COPY[data.mode]

  return (
    <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-900 dark:bg-violet-950/30 sm:px-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200">
            <Sparkles className="size-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <Label htmlFor={selectId} className="font-semibold">
              {SELF_LEARNING_LABEL}
            </Label>
            {data.mode !== "off" && (
              <p className="text-xs text-muted-foreground">{learningSinceLine(data.since, user?.timezone ?? "UTC")}</p>
            )}
          </div>
        </div>
        <SelfLearningModeSelect
          agentId={agentId}
          agentName={agentName}
          canManage={canManage}
          id={selectId}
          describedBy={detailsId}
          triggerClassName="w-full sm:w-60"
        />
      </div>
      <div id={detailsId} className="space-y-2">
        <p className="text-sm text-muted-foreground">{copy.description}</p>
        {copy.warning && <ModeWarning warning={copy.warning} />}
      </div>
      <SelfLearningArticlesSwitch agentId={agentId} canManage={canManage} />
      {actsAsSuggestOnly(data.mode, hasOwnInstructions) && (
        <p className="text-xs font-medium">{NO_OWN_INSTRUCTIONS_LINE}</p>
      )}
      {data.qualityDisabledBy && (
        <p className="text-xs font-medium">{qualityOffNotice(data.qualityDisabledBy)}</p>
      )}
      <p className="text-xs text-muted-foreground">{IN_EVERY_MODE_NOTE}</p>
    </div>
  )
}
